"use client";

import { Ref } from "react";
import { motion, MotionValue, useTransform } from "framer-motion";
import { SectionSubHeading, SectionTitle } from "@/components";
import TypedChar from "./TypedChar";

// The two headline lines, as characters, for the typed vertical copy. Kept in
// step with the <br/> in the horizontal SectionTitle below.
const TITLE_LINES = ["WE TURN IDEAS INTO", "MEASURABLE IMPACT."];
const TOTAL_CHARS = TITLE_LINES.join("").length;
// Fraction of the reveal spent before the first title letter — the subheading's
// own moment.
const LEAD = 0.12;
interface CaseStudyHeadingRailProps {
  /** The real, measured heading — the one that fades out. */
  innerRef: Ref<HTMLDivElement>;
  /** Zero-impact runway the swap is scrubbed against. */
  triggerRef: Ref<HTMLDivElement>;
  /** Measured height of the heading while horizontal, in px. */
  headingHeight: number;
  isDesktop: boolean;
  topOpacity: MotionValue<number>;
  topY: MotionValue<number>;
  /** Animated rail width: 0 at rest, the heading's height once swapped. */
  railWidth: MotionValue<number>;
  /** Typing progress, 0→1, sliced per letter below. */
  railReveal: MotionValue<number>;
  /** Static drop that seats the rotated vertical heading back into view, px. */
  railY: number;
}

// The heading column. Below lg this is exactly what it always was: a block of
// type above the cards. From lg up it claims no width at rest — so the deck
// spans the whole section and the horizontal heading sits over its head room,
// the old full-width look — then, on scroll, the horizontal heading fades out
// while a vertical one types itself in on the left and the deck slides aside.
const CaseStudyHeadingRail = ({
  innerRef,
  triggerRef,
  headingHeight,
  isDesktop,
  topOpacity,
  topY,
  railWidth,
  railReveal,
  railY
}: CaseStudyHeadingRailProps) => {
  // Subheading writes on first, in the lead slice, before the title letters.
  const subOpacity = useTransform(railReveal, [0, LEAD * 0.8], [0, 1]);

  // Running character index across both lines, so the timing is continuous from
  // the end of one line into the start of the next.
  let charIndex = 0;
  return (
    // z-10, deliberately *under* the deck (z-30): where the vertical heading and
    // a card briefly share space, the card paints over it — the way the section
    // always worked. Above the deck, the heading's box cut a notch into the
    // top-left of the first card.
    <motion.div className="relative w-full lg:z-10 lg:w-auto lg:shrink-0"
    // Explicit "100%" off desktop, not undefined: the width is a MotionValue
    // on lg (0 at rest), and framer leaves that inline width in place when the
    // prop goes away, so below lg the rail stayed 0-wide and the heading
    // wrapped a word per line. Setting it back overrides the stale value.
    style={isDesktop ? {
      width: railWidth
    } : {
      width: "100%"
    }}>
      {/* Out of flow on purpose: it exists only to be measured against, and
          must not occupy a single pixel of layout. */}
      <div ref={triggerRef} aria-hidden className="pointer-events-none absolute left-0 top-0 w-px" style={{
        height: `${headingHeight || 1}px`
      }} />

      {/* Zero-height on lg so the sticky heading pins without the rail claiming
          any vertical space of its own — the column's height comes from the
          deck beside it, which is what keeps the heading pinned for the whole
          stack. */}
      {/* top-14: high enough that the tall vertical heading keeps even breathing
          room top and bottom, low enough that it does not graze the top edge. */}
      <div className="relative lg:sticky lg:top-14 lg:h-0">
        {/* Horizontal heading — the real, measured one. Fades out on scroll. */}
        <motion.div ref={innerRef}
        // Same reasoning as the rail width: reset to visible off desktop, or a
        // stale opacity/transform from a resized-down desktop lingers and the
        // heading can render faded or shifted on mobile.
        style={isDesktop ? {
          opacity: topOpacity,
          y: topY
        } : {
          opacity: 1,
          y: 0
        }} className="relative mb-8 w-full space-y-2 lg:mb-0 lg:w-max">
          <SectionSubHeading subtitle="Featured Projects" className="text-white" />

          <SectionTitle title={<>
                WE TURN IDEAS INTO <br /> MEASURABLE IMPACT.
              </>} className="text-white" />
        </motion.div>

        {/* Vertical heading — a decorative twin that types itself in on the left
            as the horizontal one leaves. Rotated a quarter-turn and dropped back
            into view by its own width; the letters reveal one at a time. */}
        <motion.div aria-hidden style={isDesktop ? {
          rotate: -90,
          y: railY,
          transformOrigin: "top left"
        } : undefined}
        // Decorative twin: opacity-0 at rest but still laid out, so without
        // this it sat over the real heading's left words and swallowed the
        // drag — you could never select from the start of the line.
        className="pointer-events-none absolute left-0 top-0 hidden w-max space-y-2 select-none lg:block">
          <motion.div style={{
            opacity: subOpacity
          }}>
            <SectionSubHeading subtitle="Featured Projects" className="text-white" />
          </motion.div>

          <h2 className="font-black uppercase text-3xl md:text-5xl xl:text-6xl tracking-tighter text-white">
            {TITLE_LINES.map((line, li) => <span key={li} className="block">
                {line.split("").map((char, ci) => {
                const g = charIndex++;
                // Each letter owns a slice after the lead, and lights over a
                // little more than its own slice so the fade is not a hard cut.
                const from = LEAD + g / TOTAL_CHARS * (1 - LEAD);
                const to = from + (1 - LEAD) / TOTAL_CHARS * 1.6;
                return <TypedChar key={ci} char={char} progress={railReveal} from={from} to={to} />;
              })}
              </span>)}
          </h2>
        </motion.div>
      </div>
    </motion.div>
  );
};
export default CaseStudyHeadingRail;